<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'

import { clampMaterialOpacity } from '@/animation/clampMaterialOpacity'
import { MAX_SCREEN_IMAGE_COUNT, MAX_SCREEN_IMAGE_EDGE_PX } from '@/config/mockupScreenMediaLimits'
import {
  resolvedLayerScreenImageTransitionMode,
  SCREEN_IMAGE_TRANSITION_CIRCLE,
  SCREEN_IMAGE_TRANSITION_FADE,
  SCREEN_IMAGE_TRANSITION_NONE,
  SCREEN_IMAGE_TRANSITION_SLIDE_DOWN,
  SCREEN_IMAGE_TRANSITION_SLIDE_LEFT,
  SCREEN_IMAGE_TRANSITION_SLIDE_RIGHT,
  SCREEN_IMAGE_TRANSITION_SLIDE_UP,
  SCREEN_IMAGE_TRANSITION_ZOOM,
  type ScreenImageTransitionModeId,
} from '@/constants/screenImageTransition'
import { MOCKUP_DEVICE_TEMPLATES } from '@/constants/mockupDevices'
import type { Vec3 } from '@/types/animationLayer'
import { EASING_PRESETS, type EasingPreset, isEasingPreset } from '@/types/easingPreset'
import { useMockupEditorStore } from '@/stores/mockupEditor'

const store = useMockupEditorStore()
const {
  hasAnimationLayers,
  activeLayer,
  activeLayerEvaluatedPose,
  activeLayerLocked,
  activeLayerCanRemoveSelectedClip,
  selectedClipId,
  selectedKeyframeId,
  copiedKeyframePose,
} = storeToRefs(store)

const rasterTransitionSelectOptions: ReadonlyArray<{
  value: ScreenImageTransitionModeId
  labelKey: string
}> = [
  { value: SCREEN_IMAGE_TRANSITION_NONE, labelKey: 'inspector.controls.screenRasterTransitionNone' },
  { value: SCREEN_IMAGE_TRANSITION_FADE, labelKey: 'inspector.controls.screenRasterTransitionFade' },
  { value: SCREEN_IMAGE_TRANSITION_SLIDE_LEFT, labelKey: 'inspector.controls.screenRasterTransitionSlideLeft' },
  { value: SCREEN_IMAGE_TRANSITION_SLIDE_RIGHT, labelKey: 'inspector.controls.screenRasterTransitionSlideRight' },
  { value: SCREEN_IMAGE_TRANSITION_SLIDE_UP, labelKey: 'inspector.controls.screenRasterTransitionSlideUp' },
  { value: SCREEN_IMAGE_TRANSITION_SLIDE_DOWN, labelKey: 'inspector.controls.screenRasterTransitionSlideDown' },
  { value: SCREEN_IMAGE_TRANSITION_ZOOM, labelKey: 'inspector.controls.screenRasterTransitionZoom' },
  { value: SCREEN_IMAGE_TRANSITION_CIRCLE, labelKey: 'inspector.controls.screenRasterTransitionCircle' },
]

function onScreenRasterTransitionSelect(e: Event): void {
  const v = Number((e.target as HTMLSelectElement).value)
  if (!Number.isFinite(v) || v < SCREEN_IMAGE_TRANSITION_NONE || v > SCREEN_IMAGE_TRANSITION_CIRCLE) return
  store.setActiveLayerScreenImageTransitionMode(v as ScreenImageTransitionModeId)
}

/** 当前选中图层的多图过渡模式（每图层独立；未显式设置时与运行态一致，默认淡入淡出）。 */
const activeLayerScreenRasterTransitionModeUi = computed(() => {
  const layer = activeLayer.value
  if (!layer) return SCREEN_IMAGE_TRANSITION_FADE
  return resolvedLayerScreenImageTransitionMode(layer)
})

const maxScreenImageCount = MAX_SCREEN_IMAGE_COUNT
const maxScreenImageEdgePx = MAX_SCREEN_IMAGE_EDGE_PX

/** 当前选中图层上、时间轴选中的动画片段 */
const selectedClip = computed(() => {
  const layer = activeLayer.value
  const id = selectedClipId.value
  if (!layer || !id) return null
  return layer.clips.find((c) => c.id === id) ?? null
})

/**
 * @param preset - 缓动预设 id
 * @returns 用于 `<option>` 的短标签
 */
function easingLabel(preset: EasingPreset): string {
  const keyMap: Record<EasingPreset, string> = {
    linear: 'inspector.controls.easingLinear',
    'ease-in': 'inspector.controls.easingEaseIn',
    'ease-out': 'inspector.controls.easingEaseOut',
    'ease-in-out': 'inspector.controls.easingEaseInOut',
    'bounce-out': 'inspector.controls.easingBounceOut',
    'back-out': 'inspector.controls.easingBackOut',
  }
  return `${store.tUi(keyMap[preset])} (${preset})`
}

/** 当前选中 clip 内端点关键帧按时间排序 */
const selectedClipKeyframesSorted = computed(() => {
  const c = selectedClip.value
  if (!c) return []
  return [...c.keyframes].sort((a, b) => a.timeInClip - b.timeInClip)
})

const selectedKeyframeInClip = computed(() => {
  const clip = selectedClip.value
  const keyframeId = selectedKeyframeId.value
  if (!clip || !keyframeId) return null
  return clip.keyframes.find((k) => k.id === keyframeId) ?? null
})

/** 当前选中关键帧材质不透明度（0～1），供滑动条绑定 */
const selectedKeyframeMaterialOpacityUi = computed(() => {
  const kf = selectedKeyframeInClip.value
  if (!kf) return 1
  return clampMaterialOpacity(kf.materialOpacity)
})

const selectedKeyframeScreenImageIdUi = computed(() => {
  const kf = selectedKeyframeInClip.value
  if (!kf) return ''
  return kf.screenImageId ?? ''
})

const canCopySelectedKeyframePose = computed(
  () => !!selectedClipId.value && !!selectedKeyframeInClip.value,
)

const canPasteToSelectedKeyframe = computed(
  () => !!selectedClipId.value && !!selectedKeyframeInClip.value && copiedKeyframePose.value != null,
)

/**
 * @param e - 片段级 `<select>` 的 `change`
 */
function onClipEasingPresetChange(e: Event): void {
  const v = (e.target as HTMLSelectElement).value
  if (isEasingPreset(v)) store.setSelectedClipEasingPreset(v)
}

/**
 * 在 Inspector 中选中指定关键帧并对齐播放头。
 *
 * @param keyframeId - 关键帧 id
 * @param keyframeTimeInClip - 关键帧相对片段时间
 */
function jumpToClipKeyframe(keyframeId: string, keyframeTimeInClip: number): void {
  const clip = selectedClip.value
  if (!clip) return
  store.selectAnimationClip(clip.id)
  store.selectKeyframe(clip.id, keyframeId)
  store.setCurrentTime(clip.startSec + keyframeTimeInClip)
}

/**
 * 复制当前选中关键帧姿态。
 */
function copySelectedKeyframePoseFromOperations(): void {
  const clip = selectedClip.value
  const keyframe = selectedKeyframeInClip.value
  if (!clip || !keyframe) return
  store.copyKeyframePose(clip.id, keyframe.id)
}

/**
 * 粘贴到当前选中关键帧。
 */
function pasteSelectedKeyframePoseFromOperations(): void {
  const clip = selectedClip.value
  const keyframe = selectedKeyframeInClip.value
  if (!clip || !keyframe) return
  store.pasteKeyframePose(clip.id, keyframe.id)
}

const fileInputRef = ref<HTMLInputElement | null>(null)
const imagePlaneFileInputRef = ref<HTMLInputElement | null>(null)

const posRange = { min: -10, max: 10, step: 0.01 }
const rotRange = { min: -180, max: 180, step: 0.1 }
const scaleRange = { min: 0.1, max: 5, step: 0.01 }

const axes: (keyof Vec3)[] = ['x', 'y', 'z']

/** 当前图层是否为图片/视频平面 */
const isImagePlane = computed(() => activeLayer.value?.deviceTemplateId === 'image-plane')

/** 当前图层是否为文字平面 */
const isTextPlane = computed(() => activeLayer.value?.deviceTemplateId === 'text-plane')
const supportsKeyframeMaterialOpacity = computed(() => isImagePlane.value || isTextPlane.value)

/**
 * 将数值格式化为两位小数，供属性面板输入框显示。
 *
 * @param value - 原始数值
 */
function formatFixed2(value: number): string {
  if (!Number.isFinite(value)) return '0.00'
  return value.toFixed(2)
}

function openMaterialPicker(): void {
  fileInputRef.value?.click()
}

function openImagePlanePicker(): void {
  imagePlaneFileInputRef.value?.click()
}

async function onScreenMaterialFilesChange(e: Event): Promise<void> {
  const el = e.target as HTMLInputElement
  const files = el.files ? Array.from(el.files) : []
  el.value = ''
  await store.ingestScreenMaterialFileList(files)
}

function onKeyframeScreenImageSelect(e: Event): void {
  const v = (e.target as HTMLSelectElement).value
  store.setSelectedKeyframeScreenImageId(v.length > 0 ? v : null)
}

/**
 * 处理文字内容输入。
 *
 * @param e - input change 事件
 */
function onTextContentChange(e: Event): void {
  store.setActiveLayerTextContent((e.target as HTMLTextAreaElement).value)
}

/**
 * 处理字号输入。
 *
 * @param e - input change 事件
 */
function onTextFontSizeChange(e: Event): void {
  const v = Number((e.target as HTMLInputElement).value)
  if (Number.isFinite(v)) store.setActiveLayerTextFontSize(v)
}

/**
 * 处理文字颜色选择。
 *
 * @param e - color input change 事件
 */
function onTextColorChange(e: Event): void {
  store.setActiveLayerTextColor((e.target as HTMLInputElement).value)
}

function onTextFontWeightChange(e: Event): void {
  const v = (e.target as HTMLSelectElement).value
  if (v === 'normal' || v === 'bold') store.setActiveLayerTextFontWeight(v)
}

function onTextAlignChange(e: Event): void {
  const v = (e.target as HTMLSelectElement).value
  if (v === 'left' || v === 'center' || v === 'right') store.setActiveLayerTextAlign(v)
}

/**
 * @param e - range `input`
 */
function onImagePlaneKeyframeMaterialOpacityInput(e: Event): void {
  const v = Number((e.target as HTMLInputElement).value)
  if (!Number.isFinite(v)) return
  store.setSelectedKeyframeMaterialOpacity(v)
}

function numIn(
  fn: (axis: keyof Vec3, v: number) => void,
  axis: keyof Vec3,
  e: Event,
): void {
  const v = Number((e.target as HTMLInputElement).value)
  if (!Number.isFinite(v)) return
  fn(axis, v)
}

function rangeIn(
  fn: (axis: keyof Vec3, v: number) => void,
  axis: keyof Vec3,
  e: Event,
): void {
  const v = Number((e.target as HTMLInputElement).value)
  fn(axis, v)
}

/** 屏幕图列表项悬停：在列表行上方居中显示缩略图（挂到 body，避免滚动容器裁切）。 */
const screenImgHoverPreview = ref<{ src: string; anchorX: number; anchorY: number } | null>(null)

function onScreenImgListItemEnter(e: MouseEvent, textureSrc: string | null | undefined): void {
  if (!textureSrc) {
    screenImgHoverPreview.value = null
    return
  }
  const el = e.currentTarget
  if (!(el instanceof HTMLElement)) return
  const r = el.getBoundingClientRect()
  screenImgHoverPreview.value = {
    src: textureSrc,
    anchorX: r.left + r.width / 2,
    anchorY: r.top,
  }
}

function onScreenImgListItemLeave(): void {
  screenImgHoverPreview.value = null
}

/** 是否与「关键帧屏幕图」下拉同步：需已选中关键帧且图层未锁定。 */
const screenImgListRowSelectable = computed(
  () => !!selectedKeyframeInClip.value && !activeLayerLocked.value,
)

/**
 * 点击列表行：将当前关键帧绑定到该栈图 id（与 `#kf-screen-img` 下拉一致；删除按钮已 stop 不触发）。
 */
function onScreenImgListRowClick(imageId: string): void {
  if (!screenImgListRowSelectable.value) return
  store.setSelectedKeyframeScreenImageId(imageId)
}

function onScreenImgListRowKeydown(e: KeyboardEvent, imageId: string): void {
  if (!screenImgListRowSelectable.value) return
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    store.setSelectedKeyframeScreenImageId(imageId)
  }
}
</script>

<template>
  <section class="sec" :aria-label="store.tUi('inspector.controls.panelLabel')">
    <!-- 无动画图层：空状态（与效果图 2 一致） -->
    <template v-if="!hasAnimationLayers">
      <details class="acc" open>
        <summary class="sum">
          <span class="sumIco" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                fill="currentColor"
                d="M12 2L2 7l10 5 10-5-10-5zm0 2.2 6.9 3.45L12 11.1 5.1 7.65 12 4.2zM4 9.3l7 3.5v7.2l-7-3.5V9.3zm16 0v7.2l-7 3.5v-7.2l7-3.5z"
              />
            </svg>
          </span>
          {{ store.tUi('inspector.controls.selectionInfo') }}
        </summary>
        <p class="emptyTxt">{{ store.tUi('inspector.controls.pleaseSelectFirst') }}</p>
      </details>
      <hr class="hr" />
      <details class="acc" open>
        <summary class="sum">
          <span class="sumIco" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                fill="currentColor"
                d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.488.488 0 0 0-.59-.22l-2.39.96c-.52-.4-1.08-.73-1.69-.98l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.61.25-1.17.59-1.69.98l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.31-.08.63-.08.94s.02.63.06.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.52.4 1.08.73 1.69.98l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.61-.25 1.17-.59 1.69-.98l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"
              />
            </svg>
          </span>
          {{ store.tUi('inspector.controls.operations') }}
        </summary>
        <p class="emptyTxt">{{ store.tUi('inspector.controls.pleaseSelectFirst') }}</p>
      </details>
    </template>

    <!-- 有图层：完整面板（与效果图 1 一致） -->
    <template v-else-if="activeLayer">
      <details class="acc" open>
        <summary class="sum">
          <span class="sumIco" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                fill="currentColor"
                d="M12 2L2 7l10 5 10-5-10-5zm0 2.2 6.9 3.45L12 11.1 5.1 7.65 12 4.2zM4 9.3l7 3.5v7.2l-7-3.5V9.3zm16 0v7.2l-7 3.5v-7.2l7-3.5z"
              />
            </svg>
          </span>
          {{ store.tUi('inspector.controls.selectionInfo') }}
        </summary>

        <div class="panelInner">
          <div class="identityBlock">
            <p v-if="activeLayerLocked" class="lockHint">{{ store.tUi('inspector.controls.lockHint') }}</p>
            <label class="idLbl" for="layer-name">{{ store.tUi('inspector.controls.layerName') }}</label>
            <input
              id="layer-name"
              class="num"
              type="text"
              maxlength="80"
              autocomplete="off"
              :value="activeLayer.name"
              :disabled="activeLayerLocked"
              @change="store.setActiveLayerName(($event.target as HTMLInputElement).value)"
            />
            <div class="devMeta">
              <label class="devLbl" for="layer-device">{{ store.tUi('inspector.controls.device') }}</label>
              <select
                id="layer-device"
                class="devSel"
                :value="activeLayer.deviceTemplateId"
                :aria-label="store.tUi('inspector.controls.deviceTemplateAria')"
                :disabled="activeLayerLocked"
                @change="
                  store.setActiveLayerDeviceTemplate(
                    ($event.target as HTMLSelectElement).value,
                  )
                "
              >
                <option v-for="d in MOCKUP_DEVICE_TEMPLATES" :key="d.id" :value="d.id">
                  {{ d.name }} ({{ d.width }}×{{ d.height }})
                </option>
              </select>
            </div>
          </div>

          <div class="tfBlock">
            <div class="tfTitle">{{ store.tUi('inspector.controls.position') }}</div>
            <div class="axisGrid">
              <div v-for="ax in axes" :key="'p-' + ax" class="axisCol">
                <span class="axLbl">{{ ax.toUpperCase() }}</span>
                <input
                  class="num"
                  type="number"
                  :value="formatFixed2(activeLayerEvaluatedPose?.position[ax] ?? activeLayer.position[ax])"
                  :step="posRange.step"
                  :min="posRange.min"
                  :max="posRange.max"
                  :disabled="activeLayerLocked"
                  @change="numIn(store.setActiveLayerPosition, ax, $event)"
                />
                <input
                  class="slider"
                  type="range"
                  :min="posRange.min"
                  :max="posRange.max"
                  :step="posRange.step"
                  :value="activeLayerEvaluatedPose?.position[ax] ?? activeLayer.position[ax]"
                  :aria-label="store.tUi('inspector.controls.axisPosition', { axis: ax.toUpperCase() })"
                  :disabled="activeLayerLocked"
                  @input="rangeIn(store.setActiveLayerPosition, ax, $event)"
                />
              </div>
            </div>
          </div>

          <div class="tfBlock">
            <div class="tfTitle">{{ store.tUi('inspector.controls.rotation') }}</div>
            <div class="axisGrid">
              <div v-for="ax in axes" :key="'r-' + ax" class="axisCol">
                <span class="axLbl">{{ store.tUi('inspector.controls.axisRotate', { axis: ax.toUpperCase() }) }}</span>
                <input
                  class="num"
                  type="number"
                  :value="formatFixed2(activeLayerEvaluatedPose?.rotationDeg[ax] ?? activeLayer.rotationDeg[ax])"
                  :step="rotRange.step"
                  :min="rotRange.min"
                  :max="rotRange.max"
                  :disabled="activeLayerLocked"
                  @change="numIn(store.setActiveLayerRotationDeg, ax, $event)"
                />
                <input
                  class="slider"
                  type="range"
                  :min="rotRange.min"
                  :max="rotRange.max"
                  :step="rotRange.step"
                  :value="activeLayerEvaluatedPose?.rotationDeg[ax] ?? activeLayer.rotationDeg[ax]"
                  :aria-label="store.tUi('inspector.controls.axisRotate', { axis: ax.toUpperCase() })"
                  :disabled="activeLayerLocked"
                  @input="rangeIn(store.setActiveLayerRotationDeg, ax, $event)"
                />
              </div>
            </div>
          </div>

          <div class="tfBlock">
            <div class="tfTitle">{{ store.tUi('inspector.controls.scale') }}</div>
            <div class="axisGrid">
              <div v-for="ax in axes" :key="'s-' + ax" class="axisCol">
                <span class="axLbl">{{ store.tUi('inspector.controls.axisScale', { axis: ax.toUpperCase() }) }}</span>
                <input
                  class="num"
                  type="number"
                  :value="formatFixed2(activeLayerEvaluatedPose?.scale[ax] ?? activeLayer.scale[ax])"
                  :step="scaleRange.step"
                  :min="scaleRange.min"
                  :max="scaleRange.max"
                  :disabled="activeLayerLocked"
                  @change="numIn(store.setActiveLayerScale, ax, $event)"
                />
                <input
                  class="slider"
                  type="range"
                  :min="scaleRange.min"
                  :max="scaleRange.max"
                  :step="scaleRange.step"
                  :value="activeLayerEvaluatedPose?.scale[ax] ?? activeLayer.scale[ax]"
                  :aria-label="store.tUi('inspector.controls.axisScale', { axis: ax.toUpperCase() })"
                  :disabled="activeLayerLocked"
                  @input="rangeIn(store.setActiveLayerScale, ax, $event)"
                />
              </div>
            </div>
          </div>

          <div v-if="selectedClip" class="clipBlock">
            <div class="tfTitle">{{ store.tUi('inspector.controls.animationClip') }}</div>
            <p class="clipMeta">
              {{ selectedClip.startSec.toFixed(2) }}s —
              {{ (selectedClip.startSec + selectedClip.durationSec).toFixed(2) }}s
            </p>
            <div class="clipEaseRow">
              <label class="matLbl" for="clip-ease-preset">{{ store.tUi('inspector.controls.clipEasing') }}</label>
              <select
                id="clip-ease-preset"
                class="devSel"
                :value="selectedClip.easingPreset"
                :aria-label="store.tUi('inspector.controls.clipEasingAria')"
                :disabled="activeLayerLocked"
                @change="onClipEasingPresetChange"
              >
                <option v-for="p in EASING_PRESETS" :key="'clip-ease-' + p" :value="p">
                  {{ easingLabel(p) }}
                </option>
              </select>
            </div>
            <p class="clipHint">
              {{ store.tUi('inspector.controls.clipHint') }}
            </p>
            <ul class="kfList" :aria-label="store.tUi('inspector.controls.keyframesAria')">
              <li v-for="kf in selectedClipKeyframesSorted" :key="kf.id" class="kfItem">
                <div class="kfRow">
                  <button
                    type="button"
                    class="kfBtn"
                    :class="{ selected: kf.id === selectedKeyframeId }"
                    @click="jumpToClipKeyframe(kf.id, kf.timeInClip)"
                  >
                    {{ kf.timeInClip <= 0.001 ? store.tUi('inspector.controls.firstFrame') : store.tUi('inspector.controls.lastFrame') }}（t = {{ kf.timeInClip.toFixed(2) }}s）
                  </button>
                </div>
              </li>
            </ul>
            <div v-if="supportsKeyframeMaterialOpacity && selectedKeyframeInClip" class="matRow opacityKfRow">
              <label class="matLbl" for="img-plane-kf-opacity">{{ store.tUi('inspector.controls.materialOpacityLabel') }}</label>
              <input
                id="img-plane-kf-opacity"
                class="slider"
                type="range"
                min="0"
                max="1"
                step="0.01"
                :value="selectedKeyframeMaterialOpacityUi"
                :disabled="activeLayerLocked"
                :aria-valuemin="0"
                :aria-valuemax="1"
                :aria-valuenow="selectedKeyframeMaterialOpacityUi"
                :aria-label="store.tUi('inspector.controls.materialOpacitySliderAria')"
                @input="onImagePlaneKeyframeMaterialOpacityInput"
              />
              <span class="matSub">{{
                store.tUi('inspector.controls.materialOpacityValue', {
                  value: selectedKeyframeMaterialOpacityUi.toFixed(2),
                })
              }}</span>
            </div>
            <div
              v-if="activeLayer && activeLayer.screenImages.length > 0 && selectedKeyframeInClip"
              class="matRow"
            >
              <label class="matLbl" for="kf-screen-img">{{ store.tUi('inspector.controls.keyframeScreenImage') }}</label>
              <select
                id="kf-screen-img"
                class="devSel"
                :value="selectedKeyframeScreenImageIdUi"
                :disabled="activeLayerLocked"
                @change="onKeyframeScreenImageSelect"
              >
                <option value="">{{ store.tUi('inspector.controls.keyframeScreenImageInherit') }}</option>
                <option v-for="im in activeLayer.screenImages" :key="im.id" :value="im.id">{{ im.fileName }}</option>
              </select>
            </div>
          </div>

          <!-- 图片/视频平面：上传控件 + 提示 -->
          <div v-if="isImagePlane" class="matBlock">
            <p class="videoHint">{{ store.tUi('inspector.controls.imagePlaneHint') }}</p>
            <p class="videoHint">{{
              store.tUi('inspector.controls.screenImageStackHint', {
                max: String(maxScreenImageCount),
                edge: String(maxScreenImageEdgePx),
              })
            }}</p>
            <div v-if="activeLayer.screenImages.length >= 2" class="matRow">
              <label class="matLbl" for="img-plane-screen-trans">{{ store.tUi('inspector.controls.screenRasterTransition') }}</label>
              <select
                id="img-plane-screen-trans"
                class="devSel"
                :value="activeLayerScreenRasterTransitionModeUi"
                :disabled="activeLayerLocked"
                :aria-label="store.tUi('inspector.controls.screenRasterTransitionAria')"
                @change="onScreenRasterTransitionSelect"
              >
                <option v-for="opt in rasterTransitionSelectOptions" :key="opt.value" :value="String(opt.value)">
                  {{ store.tUi(opt.labelKey) }}
                </option>
              </select>
            </div>
            <div class="matRow">
              <span class="matLbl">{{ store.tUi('inspector.controls.materialName') }}</span>
              <span class="matSub">{{
                activeLayer.screenImages.length > 0
                  ? `${activeLayer.screenImages[0]?.fileName ?? ''}（${activeLayer.screenImages.length}）`
                  : activeLayer.screenMaterialFileName ?? store.tUi('inspector.controls.noMaterial')
              }}</span>
            </div>
            <ul
              v-if="activeLayer.screenImages.length > 0"
              class="screenImgList"
              :aria-label="store.tUi('inspector.controls.screenImageListAria')"
            >
              <li
                v-for="im in activeLayer.screenImages"
                :key="im.id"
                class="screenImgLi"
                :class="{
                  'screenImgLi--selectable': screenImgListRowSelectable,
                  'screenImgLi--selected': screenImgListRowSelectable && selectedKeyframeScreenImageIdUi === im.id,
                }"
                :tabindex="screenImgListRowSelectable ? 0 : -1"
                :aria-current="
                  screenImgListRowSelectable && selectedKeyframeScreenImageIdUi === im.id ? 'true' : undefined
                "
                @mouseenter="onScreenImgListItemEnter($event, im.textureSrc)"
                @mouseleave="onScreenImgListItemLeave"
                @click="onScreenImgListRowClick(im.id)"
                @keydown="onScreenImgListRowKeydown($event, im.id)"
              >
                <span class="screenImgName">{{ im.fileName }}</span>
                <button
                  type="button"
                  class="screenImgRm"
                  :disabled="activeLayerLocked"
                  @click.stop="store.removeActiveLayerScreenImageById(im.id)"
                >
                  {{ store.tUi('inspector.controls.screenImageRemove') }}
                </button>
              </li>
            </ul>
            <input
              ref="imagePlaneFileInputRef"
              class="sr"
              type="file"
              multiple
              accept="image/*,video/mp4,.mp4"
              tabindex="-1"
              @change="onScreenMaterialFilesChange"
            />
            <button type="button" class="uploadBtn" :disabled="activeLayerLocked" @click="openImagePlanePicker">
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path fill="currentColor" d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />
              </svg>
              {{ store.tUi('inspector.controls.imagePlaneUpload') }}
            </button>
            <div
              v-if="activeLayer.screenMediaType === 'video'"
              role="group"
              :aria-label="store.tUi('inspector.controls.videoTextureAria')"
            >
              <p class="videoHint">{{ store.tUi('inspector.controls.videoFollowsTimeline') }}</p>
            </div>
          </div>

          <!-- 文字平面：文字内容 + 字号 -->
          <div v-else-if="isTextPlane" class="matBlock">
            <p class="videoHint">{{ store.tUi('inspector.controls.textPlaneHint') }}</p>
            <div class="matRow">
              <label class="matLbl" for="text-content">{{ store.tUi('inspector.controls.textContent') }}</label>
              <textarea
                id="text-content"
                class="num textArea"
                maxlength="200"
                autocomplete="off"
                :value="activeLayer.textContent ?? 'Animock'"
                :disabled="activeLayerLocked"
                @change="onTextContentChange"
              ></textarea>
            </div>
            <div class="matRow">
              <label class="matLbl" for="text-font-size">{{ store.tUi('inspector.controls.textFontSize') }}</label>
              <input
                id="text-font-size"
                class="num"
                type="number"
                min="12"
                max="300"
                step="1"
                :value="activeLayer.textFontSize ?? 64"
                :disabled="activeLayerLocked"
                @change="onTextFontSizeChange"
              />
            </div>
            <div class="matRow">
              <label class="matLbl" for="text-font-weight">{{ store.tUi('inspector.controls.textFontWeight') }}</label>
              <select
                id="text-font-weight"
                class="devSel"
                :value="activeLayer.textFontWeight ?? 'bold'"
                :disabled="activeLayerLocked"
                @change="onTextFontWeightChange"
              >
                <option value="normal">{{ store.tUi('inspector.controls.textFontWeightNormal') }}</option>
                <option value="bold">{{ store.tUi('inspector.controls.textFontWeightBold') }}</option>
              </select>
            </div>
            <div class="matRow">
              <label class="matLbl" for="text-align">{{ store.tUi('inspector.controls.textAlign') }}</label>
              <select
                id="text-align"
                class="devSel"
                :value="activeLayer.textAlign ?? 'center'"
                :disabled="activeLayerLocked"
                @change="onTextAlignChange"
              >
                <option value="left">{{ store.tUi('inspector.controls.textAlignLeft') }}</option>
                <option value="center">{{ store.tUi('inspector.controls.textAlignCenter') }}</option>
                <option value="right">{{ store.tUi('inspector.controls.textAlignRight') }}</option>
              </select>
            </div>
            <div class="matRow">
              <label class="matLbl" for="text-color">{{ store.tUi('inspector.controls.textColor') }}</label>
              <div class="colorRow">
                <input
                  id="text-color"
                  class="colorSwatch"
                  type="color"
                  :value="activeLayer.textColor ?? '#888888'"
                  :disabled="activeLayerLocked"
                  @input="onTextColorChange"
                  @change="onTextColorChange"
                />
                <span class="colorHex">{{ activeLayer.textColor ?? '#888888' }}</span>
              </div>
            </div>
          </div>

          <!-- 普通设备模型：屏幕贴图材质 -->
          <div v-else class="matBlock">
            <p v-if="activeLayer.screenMediaType === 'image'" class="videoHint">{{
              store.tUi('inspector.controls.screenImageStackHint', {
                max: String(maxScreenImageCount),
                edge: String(maxScreenImageEdgePx),
              })
            }}</p>
            <div
              v-if="activeLayer.screenMediaType === 'image' && activeLayer.screenImages.length >= 2"
              class="matRow"
            >
              <label class="matLbl" for="device-screen-trans">{{ store.tUi('inspector.controls.screenRasterTransition') }}</label>
              <select
                id="device-screen-trans"
                class="devSel"
                :value="activeLayerScreenRasterTransitionModeUi"
                :disabled="activeLayerLocked"
                :aria-label="store.tUi('inspector.controls.screenRasterTransitionAria')"
                @change="onScreenRasterTransitionSelect"
              >
                <option v-for="opt in rasterTransitionSelectOptions" :key="opt.value" :value="String(opt.value)">
                  {{ store.tUi(opt.labelKey) }}
                </option>
              </select>
            </div>
            <div class="matRow">
              <span class="matLbl">{{ store.tUi('inspector.controls.materialName') }}</span>
              <span class="matSub">{{
                activeLayer.screenImages.length > 0
                  ? `${activeLayer.screenImages[0]?.fileName ?? ''}（${activeLayer.screenImages.length}）`
                  : activeLayer.screenMaterialFileName ?? store.tUi('inspector.controls.noMaterial')
              }}</span>
            </div>
            <ul
              v-if="activeLayer.screenImages.length > 0"
              class="screenImgList"
              :aria-label="store.tUi('inspector.controls.screenImageListAria')"
            >
              <li
                v-for="im in activeLayer.screenImages"
                :key="im.id"
                class="screenImgLi"
                :class="{
                  'screenImgLi--selectable': screenImgListRowSelectable,
                  'screenImgLi--selected': screenImgListRowSelectable && selectedKeyframeScreenImageIdUi === im.id,
                }"
                :tabindex="screenImgListRowSelectable ? 0 : -1"
                :aria-current="
                  screenImgListRowSelectable && selectedKeyframeScreenImageIdUi === im.id ? 'true' : undefined
                "
                @mouseenter="onScreenImgListItemEnter($event, im.textureSrc)"
                @mouseleave="onScreenImgListItemLeave"
                @click="onScreenImgListRowClick(im.id)"
                @keydown="onScreenImgListRowKeydown($event, im.id)"
              >
                <span class="screenImgName">{{ im.fileName }}</span>
                <button
                  type="button"
                  class="screenImgRm"
                  :disabled="activeLayerLocked"
                  @click.stop="store.removeActiveLayerScreenImageById(im.id)"
                >
                  {{ store.tUi('inspector.controls.screenImageRemove') }}
                </button>
              </li>
            </ul>
            <input
              ref="fileInputRef"
              class="sr"
              type="file"
              multiple
              accept="image/*,.hdr,video/mp4,.mp4"
              tabindex="-1"
              @change="onScreenMaterialFilesChange"
            />
            <button type="button" class="uploadBtn" :disabled="activeLayerLocked" @click="openMaterialPicker">
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"
                />
              </svg>
              {{ store.tUi('inspector.controls.selectMaterialFile') }}
            </button>
          </div>

          <div
            v-if="!isImagePlane && !isTextPlane && activeLayer.screenMediaType === 'video'"
            class="matBlock"
            role="group"
            :aria-label="store.tUi('inspector.controls.videoTextureAria')"
          >
            <p class="videoHint">{{ store.tUi('inspector.controls.videoFollowsTimeline') }}</p>
          </div>
        </div>
      </details>

      <hr class="hr" />

      <details class="acc" open>
        <summary class="sum">
          <span class="sumIco" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                fill="currentColor"
                d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.488.488 0 0 0-.59-.22l-2.39.96c-.52-.4-1.08-.73-1.69-.98l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.61.25-1.17.59-1.69.98l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.31-.08.63-.08.94s.02.63.06.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.52.4 1.08.73 1.69.98l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.61-.25 1.17-.59 1.69-.98l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"
              />
            </svg>
          </span>
          {{ store.tUi('inspector.controls.operations') }}
        </summary>

        <div class="ops">
          <div class="opsRow">
            <button
              type="button"
              class="op"
              :disabled="!canCopySelectedKeyframePose"
              @click="copySelectedKeyframePoseFromOperations"
            >
              {{ store.tUi('inspector.controls.copyKeyframePose') }}
            </button>
            <button
              type="button"
              class="op"
              :disabled="!canPasteToSelectedKeyframe || activeLayerLocked"
              @click="pasteSelectedKeyframePoseFromOperations"
            >
              {{ store.tUi('inspector.controls.pasteKeyframePose') }}
            </button>
          </div>
          <div class="opsRow">
            <button type="button" class="op" :disabled="activeLayerLocked" @click="store.resetActiveLayerTransform">
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M12 6V3L8 7l4 4V8c2.76 0 5 2.24 5 5 0 1.64-.8 3.09-2.03 4l1.42 1.42A6.96 6.96 0 0 0 19 13c0-3.87-3.13-7-7-7zm-7.56 11.56L3.02 16.02A9.93 9.93 0 0 1 2 12c0-5.52 4.48-10 10-10v3l5-5-5-5v3C6.48 0 2 4.48 2 10a9.95 9.95 0 0 0 2.44 6.56z"
                />
              </svg>
              {{ store.tUi('inspector.controls.resetPosition') }}
            </button>
            <button
              type="button"
              class="op"
              :disabled="!activeLayerCanRemoveSelectedClip || activeLayerLocked"
              @click="store.removeSelectedAnimationClip"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                />
              </svg>
              {{ store.tUi('inspector.controls.removeAnimation') }}
            </button>
          </div>
          <button type="button" class="op full danger" @click="store.removeActiveMockupLayer">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                fill="currentColor"
                d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
              />
            </svg>
            {{ store.tUi('inspector.controls.removeMockup') }}
          </button>
        </div>
      </details>
    </template>
    <Teleport to="body">
      <div
        v-if="screenImgHoverPreview"
        class="screenImgThumbTeleport"
        aria-hidden="true"
        :style="{
          left: `${screenImgHoverPreview.anchorX}px`,
          top: `${screenImgHoverPreview.anchorY}px`,
        }"
      >
        <img class="screenImgThumbTeleportImg" :src="screenImgHoverPreview.src" alt="" />
      </div>
    </Teleport>
  </section>
</template>

<style scoped>
.sec {
  display: grid;
  gap: 0.5rem;
  min-height: 120px;
}

.acc {
  border: 0;
}

.sum {
  cursor: pointer;
  list-style: none;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8125rem;
  font-weight: 800;
  color: var(--im-text-2);
  padding: 0.2rem 0;
}

.sum::-webkit-details-marker {
  display: none;
}

.sum::after {
  content: '';
  margin-left: auto;
  width: 0.45rem;
  height: 0.45rem;
  border-right: 2px solid var(--im-text-3);
  border-bottom: 2px solid var(--im-text-3);
  transform: rotate(-135deg);
}

.sumIco {
  display: grid;
  place-items: center;
  color: var(--im-text-3);
}

.emptyTxt {
  margin: 0.75rem 0 0.25rem;
  text-align: center;
  font-size: 0.8125rem;
  color: var(--im-text-3);
}

.hr {
  border: 0;
  border-top: 1px solid var(--im-border);
  margin: 0.35rem 0;
}

.panelInner {
  display: grid;
  gap: 1rem;
  padding-top: 0.65rem;
}

.identityBlock {
  display: grid;
  gap: 0.45rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px dashed var(--im-border);
}

.lockHint {
  margin: 0;
  border: 1px solid var(--im-border);
  border-radius: 8px;
  background: var(--im-elevated);
  padding: 0.45rem 0.55rem;
  font-size: 0.75rem;
  color: var(--im-muted);
}

.idLbl {
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--im-text);
}

.devMeta {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.devLbl {
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--im-muted-2);
}

.devSel {
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  padding: 0.45rem 0.5rem;
  font-size: 0.8125rem;
  font-weight: 650;
  color: var(--im-text);
  outline: none;
}

.devSel:focus-visible {
  border-color: var(--im-accent);
  box-shadow: 0 0 0 2px var(--im-focus-ring);
}

.tfBlock {
  display: grid;
  gap: 0.45rem;
}

.clipBlock {
  display: grid;
  gap: 0.35rem;
  padding: 0.5rem 0;
  border-top: 1px dashed var(--im-border);
}

.clipMeta {
  margin: 0;
  font-size: 0.75rem;
  color: var(--im-muted);
  font-weight: 650;
}

.clipHint {
  margin: 0;
  font-size: 0.75rem;
  color: var(--im-muted-2);
}

.kfList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.clipEaseRow {
  display: grid;
  gap: 0.35rem;
  align-items: center;
}

.kfRow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(7.5rem, 1fr);
  gap: 0.35rem;
  align-items: center;
}

.kfEase {
  width: 100%;
  min-width: 0;
  padding: 0.35rem 0.4rem;
  font-size: 0.6875rem;
}

.kfEaseNA {
  font-size: 0.75rem;
  color: var(--im-muted-2);
  text-align: center;
  padding: 0.25rem;
}

.kfBtn {
  width: 100%;
  text-align: left;
  border-radius: 8px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  padding: 0.4rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  color: var(--im-text);
}

.kfBtn:hover {
  background: var(--im-elevated);
}

.kfBtn.selected {
  border-color: var(--im-selection-border);
  box-shadow: inset 0 0 0 1px var(--im-selection-outline);
  background: var(--im-selection-bg);
}

.tfTitle {
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--im-text);
}

.axisGrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
}

@media (max-width: 300px) {
  .axisGrid {
    grid-template-columns: 1fr;
  }
}

.axisCol {
  display: grid;
  gap: 0.35rem;
}

.axLbl {
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--im-muted);
  text-transform: none;
}

.num {
  width: 100%;
  border-radius: 8px;
  border: 1px solid var(--im-border);
  padding: 0.4rem 0.45rem;
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
  outline: none;
}

.num:focus-visible {
  border-color: var(--im-accent);
  box-shadow: 0 0 0 2px var(--im-focus-ring);
}

.textArea {
  min-height: 5.5rem;
  resize: vertical;
  white-space: pre-wrap;
  line-height: 1.4;
}

.slider {
  width: 100%;
  accent-color: var(--im-accent);
}

.matBlock {
  display: grid;
  gap: 0.5rem;
  padding-top: 0.25rem;
}

.videoHint {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 650;
  color: var(--im-muted);
}

.opacityKfRow {
  margin-top: 0.55rem;
  padding-top: 0.55rem;
  border-top: 1px solid var(--im-border);
}

.matRow {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.matLbl {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--im-text);
}

.matSub {
  font-size: 0.75rem;
  color: var(--im-text-3);
}

.screenImgList {
  list-style: none;
  margin: 0.35rem 0 0.5rem;
  padding: 0;
  max-height: 9rem;
  overflow: auto;
  border: 1px solid var(--im-border);
  border-radius: var(--im-radius-sm);
  background: var(--im-surface-1);
}
.screenImgLi {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  padding: 0.35rem 0.45rem;
  border-bottom: 1px solid var(--im-border);
  font-size: 0.8rem;
}
.screenImgLi:last-child {
  border-bottom: none;
}
.screenImgLi--selectable {
  cursor: pointer;
  transition: background 0.12s ease, box-shadow 0.12s ease;
}
.screenImgLi--selectable:hover {
  background: var(--im-elevated);
}
.screenImgLi--selectable:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: -2px;
}
.screenImgLi--selected {
  background: color-mix(in srgb, var(--im-accent) 14%, var(--im-surface-1));
  box-shadow: inset 3px 0 0 var(--im-accent);
}
.screenImgLi--selectable.screenImgLi--selected:hover {
  background: color-mix(in srgb, var(--im-accent) 18%, var(--im-elevated));
}
.screenImgName {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--im-text-2);
}
.screenImgRm {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: var(--im-danger);
  font-size: 0.78rem;
  cursor: pointer;
  padding: 0.15rem 0.25rem;
  border-radius: var(--im-radius-sm);
  position: relative;
  z-index: 1;
}
.screenImgRm:hover {
  background: var(--im-danger-ghost);
}
.uploadBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  padding: 0.55rem 0.65rem;
  font-weight: 750;
  font-size: 0.8125rem;
  cursor: pointer;
  color: var(--im-text);
}

.uploadBtn:hover {
  background: var(--im-elevated);
}

.uploadBtn:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: 2px;
}

.ops {
  display: grid;
  gap: 0.45rem;
  padding-top: 0.65rem;
}

.opsRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.45rem;
}

.op {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  padding: 0.55rem 0.5rem;
  font-size: 0.8125rem;
  font-weight: 700;
  cursor: pointer;
  color: var(--im-text);
}

.op:hover:not(:disabled) {
  background: var(--im-elevated);
}

.op:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  color: var(--im-text-3);
}

.op.full {
  width: 100%;
}

.op.danger {
  border-color: color-mix(in srgb, var(--im-danger) 45%, transparent);
  color: var(--im-danger);
}

.op.danger:hover {
  background: rgba(229, 72, 77, 0.08);
}

.sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.colorRow {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.colorSwatch {
  flex-shrink: 0;
  width: 32px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid var(--im-border);
  padding: 2px;
  background: var(--im-surface);
  cursor: pointer;
}

.colorSwatch:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: 1px;
}

.colorSwatch:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.colorHex {
  font-size: 0.75rem;
  color: var(--im-muted);
  font-variant-numeric: tabular-nums;
  font-family: monospace;
}
</style>

<!-- Teleport 到 body，与 scoped 不同域 -->
<style>
.screenImgThumbTeleport {
  position: fixed;
  z-index: 10050;
  transform: translate(-50%, calc(-100% - 8px));
  pointer-events: none;
  padding: 4px;
  border-radius: 8px;
  border: 1px solid var(--im-border, rgba(0, 0, 0, 0.12));
  background: var(--im-surface, #fff);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.14);
}

.screenImgThumbTeleportImg {
  display: block;
  max-width: 160px;
  max-height: 120px;
  width: auto;
  height: auto;
  object-fit: contain;
}
</style>
